import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleIcon from "@mui/icons-material/People";
import { Contact, ContactBankAccount } from "../../../../interfaces/DTO/crm/contact";
import { usePartialUpdateContact } from "../../../../hooks/crm/useContacts";
import { formatBankHandlerCode, getAllBankHandlerCodes } from "../../../../utils/bankHandlerCodeFormatter";
import {
  accountProblem,
  accountsProblem,
  newBankAccount,
  spouseAccountsAfter,
  withOneDefault,
} from "../../../../utils/bankAccountsUtil";

interface Props {
  contact: Contact;
  /** the client's spouse - an account can be joint with them (entered once, kept on both) */
  spouse?: Contact;
}

const BANKS = getAllBankHandlerCodes();
const firstName = (c?: Contact) => c?.first_name || "בן/בת הזוג";

/**
 * חשבונות בנק – החשבונות של הלקוח (Contact.data.bank_accounts): שלו, ומשותפים עם בן/בת הזוג. חשבון משותף מוזן פעם
 * אחת ונשמר אצל שניהם; החשבון המסומן כברירת מחדל הוא זה שממולא בטפסים (הוראת קבע וכו').
 */
const ContactBankAccountsTab: React.FC<Props> = ({ contact, spouse }) => {
  const saved = useMemo(() => contact.data?.bank_accounts ?? [], [contact.data?.bank_accounts]);
  const [draft, setDraft] = useState<ContactBankAccount[] | null>(null);
  const updateContact = usePartialUpdateContact();
  const updateSpouse = usePartialUpdateContact();
  const busy = updateContact.isPending || updateSpouse.isPending;

  const changed = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);
  const problem = draft ? accountsProblem(draft) : null;

  const update = (index: number, fields: Partial<ContactBankAccount>) =>
    setDraft((d) => d && d.map((a, i) => (i === index ? { ...a, ...fields } : a)));
  const setDefault = (index: number) => setDraft((d) => d && d.map((a, i) => ({ ...a, is_default: i === index })));
  const remove = (index: number) => setDraft((d) => d && withOneDefault(d.filter((_, i) => i !== index)));
  const add = (joint: boolean) =>
    setDraft((d) => [
      ...(d ?? []),
      { ...newBankAccount((d ?? []).length === 0), joint, joint_with_contact_id: joint ? spouse?.id ?? null : null },
    ]);

  const save = async () => {
    if (!draft) return;
    const accounts = draft.length ? withOneDefault(draft) : [];
    const data: Record<string, unknown> = { ...(contact.data ?? {}) };
    if (accounts.length) data.bank_accounts = accounts;
    else delete data.bank_accounts;
    await updateContact.mutateAsync({ id: contact.id!, fields: { data } as Partial<Contact> });

    // the joint accounts follow on the spouse (the same id); one the client deleted / made their own is removed there
    if (spouse?.id && contact.id) {
      const spouseNext = spouseAccountsAfter(accounts, contact.id, spouse.data?.bank_accounts ?? []);
      if (spouseNext) {
        const spouseData: Record<string, unknown> = { ...(spouse.data ?? {}) };
        if (spouseNext.length) spouseData.bank_accounts = spouseNext;
        else delete spouseData.bank_accounts;
        await updateSpouse.mutateAsync({ id: spouse.id, fields: { data: spouseData } as Partial<Contact> });
      }
    }
    setDraft(null);
  };

  const jointLabel = (a: ContactBankAccount) =>
    a.joint ? `משותף עם ${a.joint_with_contact_id === spouse?.id ? firstName(spouse) : "בן/בת הזוג"}` : null;

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
        <Typography variant="body1" color="text.secondary">
          חשבונות בנק
        </Typography>
        {!draft && (
          <Button size="small" onClick={() => setDraft(saved)} sx={{ flexShrink: 0 }}>
            ערוך
          </Button>
        )}
      </Box>

      {draft ? (
        <>
          {draft.map((a, index) => {
            const rowProblem = accountProblem(a);
            return (
              <Box
                key={a.id}
                sx={{
                  display: "flex", flexDirection: "column", gap: 1, p: 1.5, borderRadius: 2,
                  border: "1px solid", borderColor: a.joint ? "secondary.light" : "divider",
                  backgroundColor: a.joint ? "rgba(156,39,176,0.04)" : undefined,
                }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <Autocomplete
                    size="small" sx={{ flex: "2 1 180px" }} disabled={busy}
                    options={BANKS}
                    value={BANKS.find((b) => b.value === a.bank_code) ?? null}
                    getOptionLabel={(b) => `${b.label} (${b.value})`}
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    onChange={(_, b) => update(index, { bank_code: b?.value ?? null })}
                    renderInput={(params) => <TextField {...params} label="בנק" />}
                  />
                  <TextField
                    size="small" label="סניף" value={a.branch_number ?? ""} disabled={busy}
                    sx={{ flex: "1 1 80px" }} inputProps={{ dir: "ltr", inputMode: "numeric", maxLength: 3 }}
                    onChange={(e) => update(index, { branch_number: e.target.value.replace(/\D/g, "") })}
                  />
                  <TextField
                    size="small" label="מספר חשבון" value={a.account_number ?? ""} disabled={busy}
                    sx={{ flex: "1.5 1 120px" }} inputProps={{ dir: "ltr", inputMode: "numeric", maxLength: 9 }}
                    onChange={(e) => update(index, { account_number: e.target.value.replace(/\D/g, "") })}
                  />
                  <Tooltip title="מחיקה">
                    <span>
                      <IconButton size="small" onClick={() => remove(index)} disabled={busy}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={<Radio size="small" checked={!!a.is_default} onChange={() => setDefault(index)} disabled={busy} />}
                    label="ברירת מחדל לטפסים"
                  />
                  {spouse && (
                    <FormControlLabel
                      control={
                        <Switch
                          size="small" checked={!!a.joint} disabled={busy}
                          onChange={(e) => update(index, {
                            joint: e.target.checked, joint_with_contact_id: e.target.checked ? spouse.id ?? null : null,
                          })}
                        />
                      }
                      label={`חשבון משותף עם ${firstName(spouse)}`}
                    />
                  )}
                  {rowProblem && (a.bank_code || a.branch_number || a.account_number) && (
                    <Typography variant="caption" color="error">{rowProblem}</Typography>
                  )}
                </Box>
              </Box>
            );
          })}

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" startIcon={<AddIcon />} onClick={() => add(false)} disabled={busy}>
              חשבון של {contact.first_name || "הלקוח"}
            </Button>
            {spouse && (
              <Button size="small" color="secondary" startIcon={<PeopleIcon />} onClick={() => add(true)} disabled={busy}>
                חשבון משותף עם {firstName(spouse)}
              </Button>
            )}
          </Box>
          {spouse && draft.some((a) => a.joint) && (
            <Typography variant="caption" color="text.secondary">
              חשבון משותף נשמר גם אצל {firstName(spouse)} - שינוי או מחיקה שלו מתעדכנים אצל שניכם.
            </Typography>
          )}
          {problem && draft.length > 0 && <Alert severity="warning">{problem}</Alert>}

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained" onClick={save} disabled={!changed || !!problem || busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              שמור
            </Button>
            <Button variant="outlined" onClick={() => setDraft(null)} disabled={busy}>
              חזור
            </Button>
          </Box>
          {(updateContact.isError || updateSpouse.isError) && (
            <Typography color="error" variant="body2">
              שגיאה בשמירה: {(updateContact.error ?? updateSpouse.error)?.message}
            </Typography>
          )}
        </>
      ) : saved.length === 0 ? (
        <Typography color="text.secondary">לא הוזנו חשבונות בנק</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {saved.map((a) => (
            <Box
              key={a.id}
              sx={{ display: "flex", alignItems: "center", gap: 2, px: 1.5, py: 1, borderRadius: 2, backgroundColor: "action.hover" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={600} noWrap>
                  {a.bank_code ? formatBankHandlerCode(a.bank_code) : "בנק לא ידוע"}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap dir="rtl">
                  סניף <span dir="ltr">{a.branch_number}</span> · חשבון <span dir="ltr">{a.account_number}</span>
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {a.joint && <Chip size="small" color="secondary" variant="outlined" icon={<PeopleIcon />} label={jointLabel(a)} />}
                {a.is_default && <Chip size="small" color="primary" label="ברירת מחדל" />}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ContactBankAccountsTab;
